import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { ImageIcon, Plus, Search, ExternalLink, X } from 'lucide-react';

const EXTERNAL_SITES = [
    { name: 'Dental2804 베스트작품', url: 'https://dental2804.com/bbs/index.php?mid=exhi_best&listStyle=webzine', description: '치과기공 베스트 작품 갤러리' },
    { name: 'Dental2804 회원작품', url: 'https://dental2804.com/bbs/index.php?mid=exhi_gallery&listStyle=webzine', description: '치과기공 회원 작품 갤러리' },
];

function Exhibition({ user }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '' });
    const [formImages, setFormImages] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const q = query(collection(db, 'exhibitionPosts'), where('status', '==', 'active'));
        const unsub = onSnapshot(q, snap => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setPosts(data); setLoading(false);
        });
        return () => unsub();
    }, []);

    const filtered = posts.filter(p => {
        if (filter === 'my' && p.authorId !== user?.uid) return false;
        if (searchTerm) return (p.title || '').toLowerCase().includes(searchTerm.toLowerCase());
        return true;
    });

    const handleSubmit = async () => {
        if (!formData.title.trim()) { alert('제목을 입력해주세요.'); return; }
        setSubmitting(true);
        try {
            let urls = [];
            for (const f of formImages) {
                const r = ref(storage, `exhibitions/${user.uid}/${Date.now()}_${f.name}`);
                await uploadBytes(r, f); urls.push(await getDownloadURL(r));
            }
            await addDoc(collection(db, 'exhibitionPosts'), {
                title: formData.title.trim(), description: formData.description.trim(), images: urls,
                authorId: user.uid, authorName: user.displayName || user.name || user.email || '',
                authorCompany: user.businessName || '', createdAt: serverTimestamp(), updatedAt: serverTimestamp(), status: 'active',
            });
            setFormData({ title: '', description: '' }); setFormImages([]); setShowForm(false);
        } catch (e) { alert('등록 실패: ' + e.message); }
        setSubmitting(false);
    };

    const fmtDate = ts => { if (!ts) return ''; const d = ts.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString('ko-KR'); };

    if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>로딩 중...</div>;

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 24, fontWeight: 700, margin: '0 0 20px' }}>
                <ImageIcon size={28} color="#7c3aed" /> 전시 갤러리
            </h1>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                {EXTERNAL_SITES.map(s => (
                    <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
                        style={{ padding: '8px 14px', background: '#ede9fe', borderRadius: 8, textDecoration: 'none', color: '#7c3aed', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {s.name} <ExternalLink size={12} />
                    </a>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                    {['all', 'my'].map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                            style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: filter === f ? '#ede9fe' : '#fff', color: filter === f ? '#7c3aed' : '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            {f === 'all' ? '전체' : '내 작품'}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, flex: 1, minWidth: 150 }}>
                    <Search size={16} color="#9ca3af" />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="검색" style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13 }} />
                </div>
                <button onClick={() => setShowForm(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                    <Plus size={18} /> 작품 등록
                </button>
            </div>

            {showForm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowForm(false)}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 16px' }}>작품 등록</h2>
                        <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="제목" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }} />
                        <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="설명" rows={4} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box', resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                            {formImages.map((f, i) => (
                                <div key={i} style={{ position: 'relative', width: 60, height: 60 }}>
                                    <img src={URL.createObjectURL(f)} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
                                    <button onClick={() => setFormImages(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={10} /></button>
                                </div>
                            ))}
                            {formImages.length < 5 && <label style={{ width: 60, height: 60, border: '1px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#9ca3af' }}><ImageIcon size={20} /><input type="file" accept="image/*" multiple onChange={e => { const files = Array.from(e.target.files); setFormImages(p => [...p, ...files].slice(0, 5)); }} hidden /></label>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowForm(false)} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>취소</button>
                            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>{submitting ? '등록 중...' : '등록'}</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedPost ? (
                <div>
                    <button onClick={() => setSelectedPost(null)} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', marginBottom: 16 }}>← 목록으로</button>
                    {selectedPost.images?.length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>{selectedPost.images.map((u, i) => <img key={i} src={u} alt="" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 12, objectFit: 'cover' }} />)}</div>}
                    <h2>{selectedPost.title}</h2>
                    <p style={{ color: '#6b7280', fontSize: 13 }}>{selectedPost.authorName} {selectedPost.authorCompany && `· ${selectedPost.authorCompany}`} · {fmtDate(selectedPost.createdAt)}</p>
                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{selectedPost.description}</p>
                </div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}><ImageIcon size={48} color="#d1d5db" /><p>등록된 작품이 없습니다</p></div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
                    {filtered.map(p => (
                        <div key={p.id} onClick={() => setSelectedPost(p)} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
                            {p.images?.length > 0 ? <img src={p.images[0]} alt="" style={{ width: '100%', height: 180, objectFit: 'cover' }} /> : <div style={{ width: '100%', height: 180, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={40} color="#d1d5db" /></div>}
                            <div style={{ padding: 12 }}>
                                <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600 }}>{p.title}</h3>
                                <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>{p.authorName} · {fmtDate(p.createdAt)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Exhibition;
